import { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Flex, Tag, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { orderPackOrders } from '@/apis/inventoryv2';
import { PackOrder } from '@/types/packing';
import PackDetail from '@/pages/Warehouse/pages/Packing/components/PackDetail';
import usePackEnums from '@/pages/Warehouse/pages/Packing/hooks/usePackEnums';

interface ViewPackOrderButtonProps {
  orderId: string;
}

const ViewPackOrderButton = ({ orderId }: ViewPackOrderButtonProps) => {
  const { t } = useTranslation();
  const { getPackStatus } = usePackEnums();
  const [loading, setLoading] = useState(false);
  const [packOrders, setPackOrders] = useState<PackOrder[] | null>(null);
  const [selectedPackOrder, setSelectedPackOrder] = useState<PackOrder | null>(null);
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setLoading(false);
    setPackOrders(null);
    setSelectedPackOrder(null);
    return () => requestRef.current?.abort();
  }, [orderId]);

  const handleView = async () => {
    if (!orderId || loading) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    try {
      const orders = await orderPackOrders([orderId], controller);
      if (controller.signal.aborted) return;
      if (orders.length === 1) {
        setSelectedPackOrder(orders[0]);
      } else {
        setPackOrders(orders);
      }
    } catch (error) {
      if (!controller.signal.aborted) {
        notification.error({ message: t('common.searchError') });
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  return (
    <>
      <Dropdown
        trigger={['click']}
        placement="bottomLeft"
        open={packOrders !== null}
        onOpenChange={open => {
          if (open) handleView();
          else setPackOrders(null);
        }}
        menu={{
          items: packOrders?.length ? packOrders.map(order => {
            const status = getPackStatus(order.status);
            return {
              key: order.id,
              label: (
                <Flex gap={8} align="center">
                  <span>{order.number}</span>
                  {status && <Tag color={status.color}>{status.label}</Tag>}
                </Flex>
              ),
            };
          }) : [{ key: 'empty', disabled: true, label: t('warehouse.noData') }],
          onClick: ({ key }) => {
            const order = packOrders?.find(item => item.id === key);
            if (order) setSelectedPackOrder(order);
            setPackOrders(null);
          },
        }}
      >
        <Button loading={loading}>{t('shipment.viewPackOrder')}</Button>
      </Dropdown>
      {selectedPackOrder && (
        <PackDetail
          key={selectedPackOrder.id}
          detail={selectedPackOrder}
          open
          readonly
          onClose={() => setSelectedPackOrder(null)}
          onConfirm={() => setSelectedPackOrder(null)}
        />
      )}
    </>
  );
};

export default ViewPackOrderButton;
